import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNotify } from 'react-admin';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface JobDetailsDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close */
  onClose: () => void;
  /** Title to display in the dialog header */
  title: string;
  /** The content to display (JSON string or object) */
  content: string | Record<string, unknown> | null;
}

// Process JSON recursively to parse escaped JSON strings and convert escape sequences
const processJsonRecursively = (obj: unknown): unknown => {
  if (typeof obj === 'string') {
    try {
      const parsed = JSON.parse(obj);
      return processJsonRecursively(parsed);
    } catch {
      return obj
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\t/g, '\t')
        .replace(/\\r/g, '\r')
        .replace(/\\\\/g, '\\');
    }
  } else if (Array.isArray(obj)) {
    return obj.map(processJsonRecursively);
  } else if (obj !== null && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = processJsonRecursively(value);
    }
    return result;
  }
  return obj;
};

// Custom JSON stringifier that preserves converted newlines
const customJsonStringify = (obj: unknown, indent: number = 0): string => {
  const spaces = '  '.repeat(indent);
  const nextSpaces = '  '.repeat(indent + 1);

  if (obj === null) return 'null';
  if (typeof obj === 'boolean' || typeof obj === 'number') return String(obj);
  if (typeof obj === 'string') return `"${obj}"`;
  if (Array.isArray(obj)) {
    if (obj.length === 0) return '[]';
    const items = obj
      .map(item => nextSpaces + customJsonStringify(item, indent + 1))
      .join(',\n');
    return `[\n${items}\n${spaces}]`;
  }
  if (typeof obj === 'object') {
    const keys = Object.keys(obj as Record<string, unknown>);
    if (keys.length === 0) return '{}';
    const items = keys
      .map(
        key =>
          `${nextSpaces}"${key}": ${customJsonStringify((obj as Record<string, unknown>)[key], indent + 1)}`
      )
      .join(',\n');
    return `{\n${items}\n${spaces}}`;
  }
  return String(obj);
};

/**
 * A dialog for viewing job payload or output with JSON syntax highlighting.
 * Header contains X button, JSONify toggle, and copy button (fixed, don't scroll).
 */
export const JobDetailsDialog = ({
  open,
  onClose,
  title,
  content,
}: JobDetailsDialogProps) => {
  const [prettyJsonEnabled, setPrettyJsonEnabled] = useState(true);
  const notify = useNotify();

  const isRawText =
    content !== null && typeof content === 'object' && 'rawText' in content;

  const getDisplayContent = (): string => {
    if (!content) return '';

    if (isRawText) {
      return (content as { rawText: string }).rawText;
    }

    if (typeof content === 'string') {
      if (prettyJsonEnabled) {
        try {
          const parsed = JSON.parse(content);
          const processedContent = processJsonRecursively(parsed);
          return customJsonStringify(processedContent);
        } catch {
          return content;
        }
      }
      return content;
    }

    if (prettyJsonEnabled) {
      const processedContent = processJsonRecursively(content);
      return customJsonStringify(processedContent);
    }

    return JSON.stringify(content, null, 2);
  };

  const handleCopyContent = async () => {
    try {
      const displayContent = getDisplayContent();
      await navigator.clipboard.writeText(displayContent);
      notify('Content copied to clipboard', { type: 'success' });
    } catch {
      notify('Failed to copy content', { type: 'error' });
    }
  };

  const displayContent = getDisplayContent();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="job-details-dialog-title"
    >
      <DialogTitle
        id="job-details-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {content && !isRawText && (
            <FormControlLabel
              control={
                <Switch
                  checked={prettyJsonEnabled}
                  onChange={e => setPrettyJsonEnabled(e.target.checked)}
                  size="small"
                />
              }
              label="JSONify"
              sx={{ mr: 1 }}
            />
          )}
          {content && (
            <Tooltip title="Copy content">
              <IconButton onClick={handleCopyContent} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          minHeight: 400,
          maxHeight: '70vh',
          backgroundColor: content ? '#2d2d2d' : 'transparent',
        }}
      >
        {content ? (
          <SyntaxHighlighter
            language="json"
            style={tomorrow}
            customStyle={{
              margin: 0,
              padding: 0,
              background: 'transparent',
              fontSize: '0.875rem',
              whiteSpace: 'pre',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%',
              overflow: 'auto',
            }}
            wrapLongLines
            codeTagProps={{
              style: {
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              },
            }}
          >
            {displayContent}
          </SyntaxHighlighter>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 200,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No content available
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
